import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { WORLDS } from "@/lib/worlds";
import { listingsApi } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SORTS = [
  { key: "recommended", label: "Recommended" },
  { key: "rating", label: "Top rated" },
  { key: "price_low", label: "Price: low to high" },
  { key: "price_high", label: "Price: high to low" },
];

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const world = params.get("world") || "";
  const q = params.get("q") || "";
  const [sort, setSort] = useState("recommended");
  const [listings, setListings] = useState(null);
  const [total, setTotal] = useState(null);
  const [search, setSearch] = useState(q);

  useEffect(() => {
    setSearch(q);
  }, [q]);

  useEffect(() => {
    let active = true;
    setListings(null);
    setTotal(null);
    const filter = { module: world || undefined, q: q || undefined };
    listingsApi
      .list({ ...filter, limit: 48 })
      .then((d) => {
        if (active) setListings(Array.isArray(d) ? d : []);
      })
      .catch(() => active && setListings([]));
    listingsApi
      .count(filter)
      .then((d) => active && setTotal(d?.count ?? null))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [world, q]);

  const sorted = useMemo(() => {
    if (!listings) return null;
    const arr = [...listings];
    if (sort === "rating") arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (sort === "price_low") arr.sort((a, b) => (a.price_from ?? a.price) - (b.price_from ?? b.price));
    else if (sort === "price_high") arr.sort((a, b) => (b.price_from ?? b.price) - (a.price_from ?? a.price));
    return arr;
  }, [listings, sort]);

  const setWorld = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set("world", key);
    else next.delete("world");
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search) next.set("q", search);
    else next.delete("q");
    setParams(next);
  };

  const activeWorld = WORLDS.find((w) => w.key === world);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-serif-display text-3xl font-bold">
            {activeWorld ? activeWorld.name : "Marketplace"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {activeWorld ? activeWorld.tagline : "Everything Fable +, in one place."}
          </p>
        </div>
        <form onSubmit={submitSearch} className="w-full md:max-w-sm">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={activeWorld ? activeWorld.searchPlaceholder : "Search Fable +"}
              data-testid="marketplace-search-input"
              className="w-full rounded-full border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </form>
      </div>

      {/* World chips */}
      <div className="no-scrollbar mt-6 flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setWorld("")}
          data-testid="world-chip-all"
          className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
            !world ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"
          }`}
        >
          All
        </button>
        {WORLDS.map((w) => {
          const Icon = w.icon;
          const on = w.key === world;
          return (
            <button
              key={w.key}
              onClick={() => setWorld(w.key)}
              data-testid={`world-chip-${w.key}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors"
              style={
                on
                  ? { background: w.accent, borderColor: w.accent, color: "#fff" }
                  : { borderColor: "hsl(var(--border))" }
              }
            >
              <Icon size={15} style={{ color: on ? "#fff" : w.accent }} />
              {w.name}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="marketplace-count">
          {total !== null ? `${total.toLocaleString()} result${total === 1 ? "" : "s"}` : sorted ? `${sorted.length} results` : "Loading…"}
        </p>
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={15} className="text-muted-foreground" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-44 rounded-full text-sm" data-testid="marketplace-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grid */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {sorted === null
          ? Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)
          : sorted.map((l) => <ListingCard key={l.id} listing={l} />)}
      </div>

      {sorted && sorted.length > 0 && total !== null && total > sorted.length && (
        <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="marketplace-more-note">
          Showing the first {sorted.length} of {total.toLocaleString()}. Pick a world or search to narrow things down.
        </p>
      )}

      {sorted && sorted.length === 0 && (
        <div className="py-20 text-center" data-testid="marketplace-empty">
          <p className="font-serif-display text-xl font-bold">Nothing here yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Try another world or a different search.</p>
          <Button className="mt-5 rounded-full" onClick={() => setWorld("")}>
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
}
